import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Loader2, Paperclip, Upload, Download, Trash2, FileText, Image as ImageIcon,
  File as FileIcon, ExternalLink, Folder, AlertTriangle, CheckCircle2, RefreshCw, Eye,
  UploadCloud,
} from 'lucide-react';
import AttachmentPreviewModal from '@/components/AttachmentPreviewModal';

/**
 * AttachmentsTab — Phase A of the Document Management rollout.
 *
 * Re-usable file-attachment panel. Embed on any entity page with:
 *   <AttachmentsTab api={api} scope="appraisal" scopeId={appraisalId} />
 *
 *   scope:    'appraisal' | 'property' | 'client'
 *   scopeId:  the ObjectId of that entity
 *
 * Renders:
 *   - Upload card with file picker + kind dropdown + save-template
 *     selector + live folder-path preview
 *   - Attachments table grouped by kind, with Download / Open-in-new-tab
 *     (drag to second screen) / Delete actions
 */

const formatSize = (b) => {
  if (!b) return '0 B';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
};

const kindIcon = (mime) => {
  if (!mime) return FileIcon;
  if (mime.startsWith('image/')) return ImageIcon;
  if (mime === 'application/pdf' || mime.startsWith('text/')) return FileText;
  return FileIcon;
};

const SCOPE_LABEL = {
  appraisal: 'Appraisal',
  property:  'Property',
  client:    'Client',
};

export default function AttachmentsTab({ api, scope, scopeId, title }) {
  const fileRef = useRef(null);
  const [rows, setRows] = useState([]);
  const [kinds, setKinds] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [pickedKind, setPickedKind] = useState('Scanned Notes');
  const [pickedTemplate, setPickedTemplate] = useState('');
  const [notes, setNotes] = useState('');
  const [pickedFile, setPickedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(null);  // attachment being previewed in modal
  // Bulk-upload queue — array of {id, file, status, error?}.
  // status: 'queued' | 'uploading' | 'done' | 'error'
  const [batchQueue, setBatchQueue] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [flash, setFlash] = useState(null);

  // Load attachments + dropdown data
  const load = useCallback(async () => {
    if (!scope || !scopeId) return;
    setLoading(true);
    try {
      const [att, ks, tpl] = await Promise.all([
        api.get(`/attachments?scope=${scope}&scope_id=${scopeId}`),
        api.get(`/attachments/kinds`),
        api.get(`/save-templates?scope=${scope}`),
      ]);
      setRows(att.data || []);
      setKinds(ks.data?.kinds || []);
      setTemplates(tpl.data || []);
      // Auto-select the default template for the scope
      const dflt = (tpl.data || []).find(t => t.scope === scope && t.is_default);
      if (dflt && !pickedTemplate) setPickedTemplate(dflt._id);
    } catch (e) {
      setFlash({ tone: 'error', msg: e?.response?.data?.detail || e.message });
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, scope, scopeId]);

  useEffect(() => { load(); }, [load]);

  // Live preview of folder/file path before upload
  const refreshPreview = useCallback(async () => {
    if (!pickedFile) { setPreview(null); return; }
    try {
      const { data } = await api.post('/save-templates/preview', {
        scope, scope_id: scopeId,
        kind: pickedKind,
        original_filename: pickedFile.name,
        template_id: pickedTemplate || undefined,
      });
      setPreview(data);
    } catch {
      setPreview(null);
    }
  }, [api, scope, scopeId, pickedKind, pickedTemplate, pickedFile]);

  useEffect(() => { refreshPreview(); }, [refreshPreview]);

  const onPick = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) { setPickedFile(null); return; }
    if (files.length === 1) {
      // Single-file mode keeps the legacy live-preview path
      setPickedFile(files[0]);
      setBatchQueue([]);
    } else {
      // Multi-select → drop the single-file preview path, enqueue for batch
      setPickedFile(null);
      setPreview(null);
      enqueueFiles(files);
    }
  };

  // Build the queue from a list of File objects. Reused by the drag-drop
  // handler. Auto-trigger upload — operator doesn't have to click Upload
  // again for bulk drops.
  const enqueueFiles = (files) => {
    const items = files.map((f, i) => ({
      id: `${Date.now()}-${i}-${f.name}`,
      file: f,
      status: 'queued',
    }));
    setBatchQueue(items);
    // Kick the runner on next tick so React has the queue rendered first.
    setTimeout(() => runBatch(items), 50);
  };

  // Sequentially process the queue. Each upload uses the currently-picked
  // kind + template + notes (so the operator picks once and drops 10 files).
  const runBatch = async (items) => {
    setUploading(true);
    setFlash(null);
    let ok = 0;
    let bad = 0;
    for (const item of items) {
      setBatchQueue((cur) => cur.map((q) => q.id === item.id ? { ...q, status: 'uploading' } : q));
      try {
        const fd = new FormData();
        fd.append('file', item.file);
        fd.append('scope', scope);
        fd.append('scope_id', scopeId);
        fd.append('kind', pickedKind);
        if (pickedTemplate) fd.append('template_id', pickedTemplate);
        if (notes) fd.append('notes', notes);
        await api.post('/attachments', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        setBatchQueue((cur) => cur.map((q) => q.id === item.id ? { ...q, status: 'done' } : q));
        ok += 1;
      } catch (e) {
        const msg = e?.response?.data?.detail || e.message;
        setBatchQueue((cur) => cur.map((q) => q.id === item.id ? { ...q, status: 'error', error: msg } : q));
        bad += 1;
      }
    }
    setUploading(false);
    setFlash({
      tone: bad === 0 ? 'ok' : 'error',
      msg: bad === 0
        ? `Uploaded ${ok} file${ok === 1 ? '' : 's'}`
        : `Uploaded ${ok} · failed ${bad}. See queue below for details.`,
    });
    load();
  };

  // Drag-and-drop handlers on the upload card
  const onDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer && e.dataTransfer.types.includes('Files')) setIsDragging(true);
  };
  const onDragOver = (e) => { e.preventDefault(); e.stopPropagation(); };
  const onDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Only clear when the cursor truly leaves the card (not on inner-element transitions).
    if (e.currentTarget === e.target) setIsDragging(false);
  };
  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files || []);
    if (files.length === 0) return;
    if (files.length === 1) {
      setPickedFile(files[0]);
      setBatchQueue([]);
    } else {
      enqueueFiles(files);
    }
  };

  const doUpload = async () => {
    if (!pickedFile) {
      setFlash({ tone: 'error', msg: 'Pick a file first.' });
      return;
    }
    setUploading(true);
    setFlash(null);
    try {
      const fd = new FormData();
      fd.append('file', pickedFile);
      fd.append('scope', scope);
      fd.append('scope_id', scopeId);
      fd.append('kind', pickedKind);
      if (pickedTemplate) fd.append('template_id', pickedTemplate);
      if (notes) fd.append('notes', notes);
      const { data } = await api.post('/attachments', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setFlash({ tone: 'ok', msg: `Uploaded "${data.original_filename}" (${formatSize(data.size_bytes)})` });
      setPickedFile(null);
      setNotes('');
      if (fileRef.current) fileRef.current.value = '';
      load();
    } catch (e) {
      setFlash({ tone: 'error', msg: e?.response?.data?.detail || e.message });
    } finally {
      setUploading(false);
    }
  };

  const doDelete = async (att) => {
    if (!window.confirm(`Delete "${att.original_filename}"? This removes the file from disk too.`)) return;
    try {
      await api.delete(`/attachments/${att._id}`);
      setFlash({ tone: 'ok', msg: `Deleted ${att.original_filename}` });
      load();
    } catch (e) {
      setFlash({ tone: 'error', msg: e?.response?.data?.detail || e.message });
    }
  };

  // The download URL is auth-gated — we attach the JWT token via api.defaults so we
  // can't just window.open(url). Fetch as blob then push to <a download>.
  const doDownload = async (att, openInNewTab = false) => {
    try {
      const res = await api.get(`/attachments/${att._id}/download`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: att.mime_type || 'application/octet-stream' });
      const url = URL.createObjectURL(blob);
      if (openInNewTab) {
        // Drag-to-second-screen workflow — opens in new browser window
        // for in-place viewing without leaving the CRM page.
        window.open(url, '_blank', 'noopener,noreferrer');
        // Don't revoke immediately — let the new window load.
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
      } else {
        const a = document.createElement('a');
        a.href = url;
        a.download = att.original_filename || 'attachment';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
    } catch (e) {
      setFlash({ tone: 'error', msg: e?.response?.data?.detail || e.message });
    }
  };

  // Group attachments by kind for the table
  const byKind = useMemo(() => {
    const m = {};
    for (const r of rows) (m[r.kind] = m[r.kind] || []).push(r);
    return Object.entries(m).sort(([a], [b]) => a.localeCompare(b));
  }, [rows]);

  return (
    <div className="space-y-4" data-testid="attachments-tab">
      {/* Header */}
      <div className="bg-gradient-to-br from-sky-50 to-cyan-50 border border-sky-200 rounded-md p-4 flex items-start justify-between gap-3">
        <div>
          <h3 className="text-lg font-bold text-sky-900 flex items-center gap-2 font-[Manrope]">
            <Paperclip size={18} /> {title || 'Attachments'}
          </h3>
          <p className="text-xs text-sky-800/80 mt-1 max-w-2xl">
            Upload scans, supplier docs, photos, work sheets, signed forms — anything related to this {SCOPE_LABEL[scope] || scope}. Files are stored on the server with the folder template you pick. Click <strong>Open</strong> on any row to view in a separate window (drag to second screen).
          </p>
        </div>
        <button onClick={load} data-testid="attachments-refresh-btn" className="text-sm border border-sky-300 bg-white text-sky-800 rounded-md px-3 py-1.5 hover:bg-sky-100 flex items-center gap-1 self-start">
          <RefreshCw size={13} /> Refresh
        </button>
      </div>

      {flash && (
        <div data-testid="attachments-flash" className={`text-sm rounded-md p-3 border ${flash.tone === 'ok' ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-rose-50 border-rose-200 text-rose-800'}`}>
          {flash.tone === 'ok' ? <CheckCircle2 size={14} className="inline mr-1" /> : <AlertTriangle size={14} className="inline mr-1" />}
          {flash.msg}
        </div>
      )}

      {/* Upload card */}
      <div
        className={`relative bg-white border rounded-md p-4 space-y-3 transition-colors ${isDragging ? 'border-sky-500 bg-sky-50/40' : 'border-slate-200'}`}
        data-testid="attachments-upload-card"
        onDragEnter={onDragEnter}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
      >
        {isDragging && (
          <div
            data-testid="attachments-drop-overlay"
            className="absolute inset-0 z-10 pointer-events-none rounded-md border-2 border-dashed border-sky-500 bg-sky-50/85 flex items-center justify-center"
          >
            <div className="text-center">
              <UploadCloud size={36} className="mx-auto text-sky-600 mb-2" />
              <div className="text-sm font-bold text-sky-800">Drop to upload</div>
              <div className="text-xs text-sky-600 mt-0.5">Multiple files will batch-upload with the current Kind / Template / Notes.</div>
            </div>
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
          <label className="block md:col-span-1">
            <span className="text-xs text-slate-500 font-semibold uppercase tracking-wide">File</span>
            <input
              ref={fileRef}
              type="file"
              multiple
              data-testid="attachments-file-input"
              onChange={onPick}
              className="mt-1 w-full text-xs file:mr-2 file:rounded-md file:border-0 file:bg-slate-100 file:text-slate-700 file:px-3 file:py-1.5 file:hover:bg-slate-200 file:cursor-pointer"
            />
            {pickedFile && (
              <span className="text-[11px] text-slate-500 block mt-1">
                {pickedFile.name} · {formatSize(pickedFile.size)}
              </span>
            )}
            {/* Iter182++ — explicit drop-zone affordance. The whole
                upload card already accepts drops (onDrop wired above);
                this dashed pill makes the capability discoverable
                instead of buried in a single-line tip. */}
            <div
              data-testid="attachments-dropzone-hint"
              onClick={() => fileRef.current?.click()}
              className="mt-2 cursor-pointer border border-dashed border-slate-300 hover:border-sky-400 hover:bg-sky-50/40 rounded-md px-3 py-2.5 flex items-center gap-2 text-[11px] text-slate-500 transition-colors"
              title="Drag &amp; drop files anywhere on this card, or click to browse"
            >
              <UploadCloud size={16} className="text-slate-400 flex-shrink-0" />
              <span>
                <strong className="text-slate-700">Drag &amp; drop files here</strong> — or click to browse. Multi-select for bulk upload (all use the Kind / Template / Notes on the right).
              </span>
            </div>
          </label>
          <label className="block">
            <span className="text-xs text-slate-500 font-semibold uppercase tracking-wide">Document kind</span>
            <select
              data-testid="attachments-kind-select"
              value={pickedKind}
              onChange={(e) => setPickedKind(e.target.value)}
              className="mt-1 w-full border border-slate-300 rounded-md px-2 py-1.5 text-sm"
            >
              {kinds.map((k) => <option key={k} value={k}>{k}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-xs text-slate-500 font-semibold uppercase tracking-wide">Save template (folder hierarchy)</span>
            <select
              data-testid="attachments-template-select"
              value={pickedTemplate}
              onChange={(e) => setPickedTemplate(e.target.value)}
              className="mt-1 w-full border border-slate-300 rounded-md px-2 py-1.5 text-sm"
            >
              <option value="">— Default fallback —</option>
              {templates.map((t) => (
                <option key={t._id} value={t._id}>
                  {t.name} {t.is_default ? '·★' : ''}
                </option>
              ))}
            </select>
          </label>
        </div>
        <label className="block">
          <span className="text-xs text-slate-500 font-semibold uppercase tracking-wide">Notes (optional)</span>
          <input
            type="text"
            data-testid="attachments-notes-input"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="e.g. Roof sketch from 14 Feb site visit"
            className="mt-1 w-full border border-slate-300 rounded-md px-2 py-1.5 text-sm"
          />
        </label>

        {/* Live folder-path preview */}
        {preview && (
          <div className="bg-slate-50 border border-slate-200 rounded-md p-2 text-[11px] text-slate-600 font-mono" data-testid="attachments-path-preview">
            <Folder size={12} className="inline mr-1 text-slate-400" />
            <span className="text-slate-500">Will save to:</span>{' '}
            <span className="text-slate-800">{preview.folder}/{preview.filename}</span>
          </div>
        )}

        <div className="flex justify-end">
          <button
            data-testid="attachments-upload-btn"
            onClick={doUpload}
            disabled={uploading || !pickedFile}
            className="bg-sky-600 text-white text-sm font-semibold rounded-md px-4 py-1.5 hover:bg-sky-700 disabled:opacity-50 flex items-center gap-2"
          >
            {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
            {uploading ? 'Uploading…' : 'Upload'}
          </button>
        </div>

        {/* Batch progress queue (shown when multi-select / drag-drop) */}
        {batchQueue.length > 0 && (
          <div data-testid="attachments-batch-queue" className="border-t border-slate-100 pt-3 space-y-1.5">
            <div className="flex items-center justify-between text-xs">
              <span className="font-semibold text-slate-700">
                Batch upload ({batchQueue.filter(q => q.status === 'done').length}/{batchQueue.length})
              </span>
              {!uploading && (
                <button
                  data-testid="attachments-clear-queue-btn"
                  onClick={() => setBatchQueue([])}
                  className="text-[11px] text-slate-500 hover:text-slate-900 underline"
                >
                  Clear queue
                </button>
              )}
            </div>
            <div className="max-h-40 overflow-y-auto space-y-1 thin-scroll">
              {batchQueue.map((q) => (
                <div
                  key={q.id}
                  data-testid={`attachments-queue-item-${q.status}`}
                  className={`flex items-center gap-2 text-xs px-2 py-1 rounded ${
                    q.status === 'done'      ? 'bg-emerald-50 text-emerald-800' :
                    q.status === 'error'     ? 'bg-rose-50 text-rose-800' :
                    q.status === 'uploading' ? 'bg-sky-50 text-sky-800' :
                                               'bg-slate-50 text-slate-600'
                  }`}
                >
                  {q.status === 'done' && <CheckCircle2 size={11} />}
                  {q.status === 'error' && <AlertTriangle size={11} />}
                  {q.status === 'uploading' && <Loader2 size={11} className="animate-spin" />}
                  {q.status === 'queued' && <span className="w-2.5 h-2.5 rounded-full border border-slate-400" />}
                  <span className="flex-1 truncate" title={q.file.name}>{q.file.name}</span>
                  <span className="text-[10px] text-slate-400 flex-shrink-0">{formatSize(q.file.size)}</span>
                  {q.error && (
                    <span className="text-[10px] text-rose-600 italic truncate max-w-[160px]" title={q.error}>{q.error}</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Existing attachments grouped by kind */}
      {loading ? (
        <div className="flex items-center gap-2 text-sm text-slate-500 p-6">
          <Loader2 size={14} className="animate-spin" /> Loading attachments…
        </div>
      ) : rows.length === 0 ? (
        <div data-testid="attachments-empty" className="bg-slate-50 border border-slate-200 rounded-md p-6 text-sm text-slate-600 text-center">
          No attachments yet. Upload your first scan, photo, or supplier document above.
        </div>
      ) : (
        <div className="space-y-3" data-testid="attachments-list">
          {byKind.map(([kind, items]) => (
            <div key={kind} className="bg-white border border-slate-200 rounded-md overflow-hidden">
              <div className="bg-slate-100 px-3 py-1.5 text-xs font-bold text-slate-700 uppercase tracking-wide flex items-center justify-between">
                <span>{kind}</span>
                <span className="text-slate-500 normal-case">{items.length} file{items.length === 1 ? '' : 's'}</span>
              </div>
              <table className="w-full text-sm">
                <tbody>
                  {items.map((att) => {
                    const Icon = kindIcon(att.mime_type);
                    return (
                      <tr key={att._id} data-testid={`attachment-row-${att._id}`} className="border-t border-slate-100 hover:bg-slate-50/50">
                        <td className="p-2 px-3 w-8"><Icon size={16} className="text-slate-400" /></td>
                        <td className="p-2 px-3">
                          <div className="text-sm font-semibold text-slate-800">{att.original_filename}</div>
                          <div className="text-[10px] text-slate-400 font-mono truncate max-w-[480px]" title={att.rel_path}>
                            {att.rel_path}
                          </div>
                          {att.notes && <div className="text-[11px] text-slate-500 italic mt-0.5">{att.notes}</div>}
                        </td>
                        <td className="p-2 px-3 text-xs text-slate-500 w-20 text-right">{formatSize(att.size_bytes)}</td>
                        <td className="p-2 px-3 text-xs text-slate-500 w-32 hidden md:table-cell">
                          {att.uploaded_at ? new Date(att.uploaded_at).toLocaleDateString() : '—'}
                        </td>
                        <td className="p-2 px-3 text-right w-52 whitespace-nowrap">
                          <button
                            data-testid={`attachment-preview-${att._id}`}
                            onClick={() => setPreviewing(att)}
                            className="text-[11px] text-indigo-700 hover:text-indigo-900 hover:underline inline-flex items-center gap-1 mr-2"
                            title="Preview in-app (side-by-side viewer with zoom + pop-out for second screen)"
                          >
                            <Eye size={12} /> Preview
                          </button>
                          <button
                            data-testid={`attachment-open-${att._id}`}
                            onClick={() => doDownload(att, true)}
                            className="text-[11px] text-sky-700 hover:text-sky-900 hover:underline inline-flex items-center gap-1 mr-2"
                            title="Open in a new browser window — drag to your second screen for reference."
                          >
                            <ExternalLink size={12} /> Open
                          </button>
                          <button
                            data-testid={`attachment-download-${att._id}`}
                            onClick={() => doDownload(att, false)}
                            className="text-[11px] text-slate-600 hover:text-slate-900 hover:underline inline-flex items-center gap-1 mr-2"
                          >
                            <Download size={12} /> Save
                          </button>
                          <button
                            data-testid={`attachment-delete-${att._id}`}
                            onClick={() => doDelete(att)}
                            className="text-[11px] text-rose-600 hover:text-rose-900 hover:underline inline-flex items-center gap-1"
                          >
                            <Trash2 size={12} /> Delete
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}

      {/* In-app side-by-side preview modal (Phase C). Pop-out button
          inside opens the same blob in a new window for second-screen
          reference workflow. */}
      {previewing && (
        <AttachmentPreviewModal
          api={api}
          attachment={previewing}
          onClose={() => setPreviewing(null)}
        />
      )}
    </div>
  );
}
